"use client";

import { useEffect, useState } from "react";

import { cn } from "@/lib/utils";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  type CarouselApi,
} from "@/shadcn/ui/carousel";
import ProductItem from "./product/ProductItem";

import { Product } from "@/lib/shopify/types";

export default function CollectionCarousel({
  items,
  size,
}: {
  items: Product[];
  size: "sm" | "md" | "lg";
}) {
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);

  useEffect(() => {
    if (!api) return;

    const onSelect = () => {
      setCurrent(api.selectedScrollSnap());
      setCanPrev(api.canScrollPrev());
      setCanNext(api.canScrollNext());
    };

    onSelect();
    api.on("select", onSelect);
    api.on("reInit", onSelect);
    return () => {
      api.off("select", onSelect);
      api.off("reInit", onSelect);
    };
  }, [api]);

  if (!items?.length) {
    return null;
  }

  return (
    <Carousel
      className="w-full [&_>_div]:!overflow-visible"
      setApi={setApi}
      opts={{
        align: "start",
        skipSnaps: false,
      }}
    >
      <CarouselContent className="w-full md:gap-x-[2rem] -ml-[2rem]">
        {items?.map((item, index) => (
          <CarouselItem
            key={index}
            className="basis-1/1 md:basis-1/2 lg:basis-1/4 pl-[2rem] relative select-none max-w-full"
          >
            <ProductItem
              item={item}
              isInteractive={true}
              showLabel={true}
              size={size}
            />
          </CarouselItem>
        ))}
      </CarouselContent>

      <div className="w-full flex justify-between mt-[2rem] items-center">
        {/* progress bar */}
        <div className="w-full max-w-[31rem] lg:max-w-[55rem] h-[.5rem] bg-black/5 relative">
          {/* lg */}
          <div
            className="hidden lg:block absolute left-0 top-0 h-full bg-[#1E3739] transition-all"
            style={{
              width: `${((current + 3) / items.length) * 100}%`,
            }}
          ></div>
          {/* md */}
          <div
            className="hidden md:block lg:hidden absolute left-0 top-0 h-full bg-[#1E3739] transition-all"
            style={{
              width: `${((current + 2) / items.length) * 100}%`,
            }}
          ></div>
          {/* sm */}
          <div
            className="md:hidden absolute left-0 top-0 h-full bg-[#1E3739] transition-all"
            style={{
              width: `${((current + 1) / items.length) * 100}%`,
            }}
          ></div>
          {/*  */}
        </div>
        {/* end progress bar */}

        {/* controls */}
        <div className="w-fit flex items-end justify-end flex-shrink-0">
          <div
            className={cn(
              "w-full flex gap-[1rem] px-[1rem] py-[.75rem] relative border-[2px] border-white rounded-full",
              "after:absolute after:-top-[1px] after:-left-[1px] after:-right-[1px] after:-bottom-[1px] after:border after:border-[#D3D3D2] after:rounded-full after:pointer-events-none"
            )}
          >
            <button
              onClick={() => api?.scrollPrev()}
              className="w-[6rem] h-[6rem] flex-shrink-0 rounded-full flex items-center justify-center bg-[#202223] transition-all text-white disabled:bg-[#202223]/10 disabled:text-[#202223] disabled:cursor-not-allowed"
              disabled={!api || !canPrev}
              aria-label="Vorherige Folie"
            >
              <svg
                viewBox="0 0 12 20"
                xmlns="http://www.w3.org/2000/svg"
                className="w-[1.25rem] h-auto flex-shrink-0 -translate-x-[.125rem]"
              >
                <path
                  d="M2.47955e-05 10L10 20L11.4 18.6L2.80002 10L11.4 1.4L10 0L2.47955e-05 10Z"
                  fill="currentColor"
                />
              </svg>
            </button>

            <button
              onClick={() => api?.scrollNext()}
              className="w-[6rem] h-[6rem] flex-shrink-0 rounded-full flex items-center justify-center bg-[#202223] transition-all text-white disabled:bg-[#202223]/10 disabled:text-[#202223] disabled:cursor-not-allowed"
              disabled={!api || !canNext}
              aria-label="Nächste Folie"
            >
              <svg
                viewBox="0 0 12 20"
                xmlns="http://www.w3.org/2000/svg"
                className="rotate-180 w-[1.25rem] h-auto flex-shrink-0 translate-x-[.125rem]"
              >
                <path
                  d="M2.47955e-05 10L10 20L11.4 18.6L2.80002 10L11.4 1.4L10 0L2.47955e-05 10Z"
                  fill="currentColor"
                />
              </svg>
            </button>
          </div>
        </div>
        {/* end controls */}
      </div>
    </Carousel>
  );
}
